import React from 'react'
import { useCallback, useLayoutEffect, useRef } from 'react'
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import time from '../../utils/time'
import themeColors from '../../theme/colors'

am4core.useTheme(am4themes_animated);

const styles = {
    chartDiv: () => ({
        width: "100%",
        height: 500,
    })
}

const AmLineChart = ({
    data,
    seriesNames,
    amchartId,
    isDateAxis,
    styleProps,
    renderedCharts
}) => {
    const chart = useRef(null);

    /**
     * configure series tooltip & labels
     * @param series
     */
    const configureSeries = useCallback((series) => {
        series.stroke = am4core.color(styleProps.lineStroke)
        series.strokeWidth = 3
        series.tooltip.label.maxWidth = 300
        series.tooltip.label.wrap = true
        series.tooltip.getFillFromObject = false
        series.tooltip.background.fill = am4core.color(styleProps.tooltipBg)
        series.tooltip.label.fill = am4core.color(styleProps.tooltipColor)
        series.tooltip.label.fontSize = 16
        series.tooltip.label.fontWeight = 'bold'
    })

    /**
     * configure bullet appearance
     * @param bullet
     */
    const configureBullets = useCallback((bullet) => {
        bullet.fill = am4core.color(styleProps.bulletColor)
        bullet.stroke = am4core.color(styleProps.bulletStroke)
        bullet.tooltipText = "{extra}"
        bullet.showTooltipOn = "hover"
        if (styleProps.bulletRadius) bullet.circle.radius = styleProps.bulletRadius
    })

    useLayoutEffect(() => {
        // renderedCharts.current[amchartId] = false

        let x = am4core.create(`chartdiv-line-${amchartId}`, am4charts.XYChart);

        const chartData = data // processData(resultSet, subqueryProps)

        // configure category axis
        let categoryAxis
        if (isDateAxis) {
            categoryAxis = x.xAxes.push(new am4charts.DateAxis());
            categoryAxis.baseInterval = {
                timeUnit: "day",
                count: 1,
            };
            categoryAxis.dateFormats.setKey("day", "d-MMM")
            categoryAxis.renderer.labels.template.location = 0.5
        } else {
            categoryAxis = x.xAxes.push(new am4charts.CategoryAxis());
            categoryAxis.dataFields.category = "x";
        }
        categoryAxis.dataFields.extra = "extra"
        categoryAxis.renderer.minGridDistance = 0;
        categoryAxis.renderer.grid.template.disabled = true
        categoryAxis.renderer.labels.template.fontSize = 16
        categoryAxis.renderer.labels.template.fill = am4core.color(styleProps.axisColor)

        // rotate date axis labels (fii)
        if (styleProps.diagonalDates) {
            categoryAxis.renderer.labels.template.rotation = -45
            categoryAxis.renderer.labels.template.dx = -25;
            categoryAxis.renderer.labels.template.dy = -5;
            categoryAxis.renderer.labels.template.fontSize = 14
            categoryAxis.renderer.labels.template.stroke = am4core.color(styleProps.axisColor)
            categoryAxis.renderer.labels.template.strokeWidth = 0.2
        }

        // configure value axis
        let valueAxis = x.yAxes.push(new am4charts.ValueAxis());
        // show or hide the grid and value axis
        if (styleProps.showGrid) {
            valueAxis.renderer.minGridDistance = 25
            valueAxis.renderer.grid.template.strokeOpacity = .4
            valueAxis.renderer.grid.template.stroke = am4core.color(styleProps.yGridColor)
        } else {
            valueAxis.renderer.grid.template.disabled = true
            valueAxis.renderer.labels.template.disabled = true
        }
        valueAxis.renderer.labels.template.fontSize = 16
        valueAxis.renderer.labels.template.fill = am4core.color(styleProps.axisColor)
        valueAxis.extraMax = 0.1

        // configure appearance for series
        seriesNames.map(sItem => {
            let series = x.series.push(new am4charts.LineSeries());
            series.name = sItem.title || sItem.key;
            series.dataFields.valueY = sItem.key;
            series.dataFields.extra = "extra"

            let dataFieldX = isDateAxis ? 'dateX' : 'categoryX'
            series.dataFields[dataFieldX] = "x";

            let valueLabel = series.bullets.push(new am4charts.LabelBullet());
            valueLabel.label.text = '{valueY}';
            valueLabel.label.fontSize = 18;
            valueLabel.label.dy = -25;
            valueLabel.label.dx = 0;

            valueLabel.label.fill = am4core.color(styleProps.textColor)
            if (styleProps.boldText) valueLabel.label.fontWeight = 'bold'

            const bullet = series.bullets.push(new am4charts.CircleBullet());

            configureSeries(series)
            configureBullets(bullet)
        })

        x.cursor = new am4charts.XYCursor();
        x.maskBullets = false
        x.data = isDateAxis ? chartData : time.formatTimeStrings(chartData)// updatedPivot

        // x.legend = new am4charts.Legend();
        x.logo.disabled = true

        x.events.on("ready", () => {
            renderedCharts.current[amchartId] = true
            // console.log('LINE RENDERED');
        })

        chart.current = x;

        return () => {
            x.dispose();
        };
    }, []);

    return (
        <div id={`chartdiv-line-${amchartId}`} style={styles.chartDiv()} />
    );
}
AmLineChart.defaultProps = {
    styleProps: {
        lineStroke: themeColors.CYAN,
        bulletColor: '#000',
        bulletStroke: themeColors.CYAN,
        tooltipBg: themeColors.CYAN,
        tooltipColor: '#000',
        diagonalDates: false,
        textColor: themeColors.LIGHT_GREY,
        boldText: false,
        showGrid: false,
    }
}

export default AmLineChart;